// 3a · Universal Intake — Shared "Add Information" control + AttachmentsPanel.
// 3b · Extended with real Choose-Existing picker, Take-Photo (getUserMedia),
//      Scan-Document (multi-page camera capture), and resumable chunked upload.
// Rendered wherever a record wants intake. Method chips filter to what the
// caller passes as `capabilities` (subset of the universal set).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB
const CHUNK_THRESHOLD = 4 * 1024 * 1024; // Files > 4 MB → resumable path.

const METHOD_META = {
  manual:      { label: "Enter Manually",    icon: "✎" },
  paste:       { label: "Paste",             icon: "❐" },
  upload:      { label: "Upload",            icon: "↑" },
  multi:       { label: "Multi-Upload",      icon: "⇈" },
  scan:        { label: "Scan",              icon: "⌷" },
  photo:       { label: "Photo",             icon: "◉" },
  existing:    { label: "Choose Existing",   icon: "☰" },
  import_csv:  { label: "Import CSV",        icon: "▤" },
  attach:      { label: "Attach to Another", icon: "⇗" },
  request_info:{ label: "Request Information", icon: "✉" },
};
const ALL_METHODS = Object.keys(METHOD_META);

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
export default function InformationIntake({
  recordType, recordId, category = "other", confidentiality = "internal",
  capabilities = ALL_METHODS, label = "Add Information",
  onChanged,
}) {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const methods = useMemo(() => capabilities.filter((k) => METHOD_META[k]), [capabilities]);

  return (
    <div data-testid={`intake-${recordType}-${recordId}`} style={{ display: "inline-block" }}>
      <button data-testid="intake-button" onClick={() => setOpen((o) => !o)}
              style={{ padding: "8px 14px", borderRadius: 4, cursor: "pointer",
                       background: "#12233F", color: "#F5F1E8",
                       border: "2px solid #C6A15B", fontSize: 12,
                       letterSpacing: 1, textTransform: "uppercase",
                       fontFamily: "'Space Mono', monospace" }}>
        {label}
      </button>
      {open && (
        <div data-testid="intake-method-chips"
             style={{ marginTop: 8, background: "white", padding: 10,
                      borderRadius: 4, border: "1px solid #e2e8f0",
                      display: "flex", flexWrap: "wrap", gap: 6 }}>
          {methods.map((m) => (
            <button key={m} data-testid={`intake-chip-${m}`}
                    onClick={() => { setDialog(m); setOpen(false); }}
                    style={{ padding: "6px 12px", borderRadius: 9999,
                             background: "#f8fafc", border: "1px solid #cbd5e1",
                             cursor: "pointer", fontSize: 12,
                             color: "#0f172a", fontWeight: 600 }}>
              <span style={{ marginRight: 6 }}>{METHOD_META[m].icon}</span>
              {METHOD_META[m].label}
            </button>
          ))}
        </div>
      )}
      {dialog && (
        <IntakeDialog method={dialog} recordType={recordType} recordId={recordId}
                       category={category} confidentiality={confidentiality}
                       onClose={() => setDialog(null)}
                       onDone={() => { setDialog(null); onChanged && onChanged(); }} />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Resumable chunked upload client (auto-fallback to /upload for small files)
// ---------------------------------------------------------------------------
async function chunkedUpload({ file, recordType, recordId, category, confidentiality,
                                projectId, label, onProgress }) {
  if (file.size < CHUNK_THRESHOLD) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("record_type", recordType);
    fd.append("record_id", recordId);
    fd.append("category", category);
    fd.append("confidentiality", confidentiality || "internal");
    if (projectId) fd.append("project_id", projectId);
    if (label) fd.append("label", label);
    const r = await axios.post(`${API}/attachments/upload`, fd, {
      withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => onProgress && onProgress(e.loaded / (e.total || file.size)),
    });
    return r.data;
  }
  // Resumable path.
  const init = await axios.post(`${API}/attachments/uploads/init`, {
    record_type: recordType, record_id: recordId, category,
    confidentiality: confidentiality || "internal",
    file_name: file.name, mime: file.type || "application/octet-stream",
    total_size: file.size, chunk_size: CHUNK_SIZE,
    project_id: projectId || null, label: label || null,
  }, { withCredentials: true });
  const { upload_id: uploadId, total_chunks: total } = init.data;
  for (let i = 0; i < total; i += 1) {
    const start = i * CHUNK_SIZE;
    const blob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
    const fd = new FormData();
    fd.append("chunk_index", String(i));
    fd.append("file", blob, `chunk_${i}`);
    // Retry once on transient network failure.
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        await axios.post(`${API}/attachments/uploads/${uploadId}/chunk`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        lastErr = null;
        break;
      } catch (e) {
        lastErr = e;
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    if (lastErr) throw lastErr;
    onProgress && onProgress((i + 1) / total);
  }
  const done = await axios.post(`${API}/attachments/uploads/${uploadId}/complete`,
                                  {}, { withCredentials: true });
  return done.data;
}


// ---------------------------------------------------------------------------
// Dialog dispatcher
// ---------------------------------------------------------------------------
function IntakeDialog({ method, recordType, recordId, category, confidentiality, onClose, onDone }) {
  if (method === "existing" || method === "attach")
    return <AttachExistingDialog recordType={recordType} recordId={recordId}
              category={category} onClose={onClose} onDone={onDone}
              modeAttach={method === "attach"} />;
  if (method === "photo")
    return <TakePhotoDialog recordType={recordType} recordId={recordId}
              category={category === "other" ? "photo_progress" : category}
              onClose={onClose} onDone={onDone} />;
  if (method === "scan")
    return <ScanDocumentDialog recordType={recordType} recordId={recordId}
              category={category === "other" ? "scan_form" : category}
              onClose={onClose} onDone={onDone} />;
  return <SimpleIntakeDialog method={method} recordType={recordType} recordId={recordId}
            category={category} confidentiality={confidentiality}
            onClose={onClose} onDone={onDone} />;
}


// ---------------------------------------------------------------------------
// Simple methods: paste / manual / upload / multi / import_csv / request_info
// ---------------------------------------------------------------------------
function SimpleIntakeDialog({ method, recordType, recordId, category, confidentiality, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [file, setFile] = useState(null);
  const [multiFiles, setMultiFiles] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [signedUrl, setSignedUrl] = useState(null);
  const [pct, setPct] = useState(0);

  const withAuth = { withCredentials: true };

  const run = async () => {
    setBusy(true); setPct(0);
    try {
      if (method === "paste") {
        await axios.post(`${API}/attachments/paste`, {
          record_type: recordType, record_id: recordId, category,
          confidentiality, content: pasteText,
        }, withAuth);
        toast.success("Pasted content stored");
      } else if (method === "upload") {
        if (!file) { toast.error("Pick a file"); setBusy(false); return; }
        await chunkedUpload({
          file, recordType, recordId, category, confidentiality,
          onProgress: setPct,
        });
        toast.success("Attachment stored");
      } else if (method === "multi") {
        if (!multiFiles.length) { toast.error("Pick files"); setBusy(false); return; }
        for (let i = 0; i < multiFiles.length; i += 1) {
          await chunkedUpload({
            file: multiFiles[i], recordType, recordId, category, confidentiality,
            onProgress: (p) => setPct((i + p) / multiFiles.length),
          });
        }
        toast.success(`${multiFiles.length} attachment${multiFiles.length === 1 ? "" : "s"} stored`);
      } else if (method === "manual") {
        await axios.post(`${API}/attachments/manual`, {
          record_type: recordType, record_id: recordId, category,
          schema_hint: "generic", payload: { note: pasteText },
        }, withAuth);
        toast.success("Manual entry stored — confirm downstream to merge.");
      } else if (method === "import_csv") {
        if (!file) { toast.error("Pick a CSV"); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("record_type", recordType);
        fd.append("record_id", recordId);
        await axios.post(`${API}/attachments/import`, fd,
                          { ...withAuth, headers: { "Content-Type": "multipart/form-data" } });
        toast.success("CSV imported as unverified preview. Confirm before posting.");
      } else if (method === "request_info") {
        const r = await axios.post(`${API}/attachments/request`, {
          record_type: recordType, record_id: recordId,
          required_categories: [category], instructions, ttl_days: 14,
        }, withAuth);
        setSignedUrl(r.data.url);
        setBusy(false);
        return; // keep dialog open to show URL
      }
      onDone && onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d?.message || d?.code || "Intake failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalFrame method={method} recordType={recordType} recordId={recordId} category={category} onClose={onClose}>
      <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
        {(method === "paste" || method === "manual") && (
          <textarea data-testid="intake-paste-textarea" rows={7}
                    value={pasteText} onChange={(e) => setPasteText(e.target.value)}
                    placeholder="Paste or describe here…"
                    style={{ padding: 10, border: "1px solid #cbd5e1",
                             borderRadius: 4, fontSize: 13, fontFamily: "inherit" }} />
        )}
        {(method === "upload" || method === "import_csv") && (
          <input type="file" data-testid="intake-file-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  style={{ padding: 8, border: "1px solid #cbd5e1", borderRadius: 4 }} />
        )}
        {method === "multi" && (
          <input type="file" multiple data-testid="intake-multi-file-input"
                  onChange={(e) => setMultiFiles(Array.from(e.target.files || []))}
                  style={{ padding: 8, border: "1px solid #cbd5e1", borderRadius: 4 }} />
        )}
        {busy && pct > 0 && (
          <div data-testid="intake-progress" style={{ height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${Math.round(pct * 100)}%`, height: "100%", background: "#C6A15B",
                          transition: "width 200ms ease" }} />
          </div>
        )}
        {method === "request_info" && !signedUrl && (
          <>
            <textarea data-testid="intake-instructions" rows={4}
                      value={instructions} onChange={(e) => setInstructions(e.target.value)}
                      placeholder="What do you need from the other person?"
                      style={{ padding: 10, border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13 }} />
            <p style={{ fontSize: 11, color: "#64748b" }}>
              A single-use, expiring signed link will be minted (14-day TTL). Their upload
              attaches back to THIS record — never a duplicate.
            </p>
          </>
        )}
        {signedUrl && (
          <div data-testid="intake-signed-url" style={{ padding: 10, background: "#f8fafc", borderRadius: 4 }}>
            <p style={{ fontSize: 12, color: "#475569" }}>Share this link:</p>
            <code style={{ fontSize: 11, wordBreak: "break-all" }}>{signedUrl}</code>
          </div>
        )}
      </div>
      <ModalActions busy={busy} onClose={onClose} onSubmit={run} />
    </ModalFrame>
  );
}


// ---------------------------------------------------------------------------
// Attach-Existing / Choose-Existing picker
// ---------------------------------------------------------------------------
function AttachExistingDialog({ recordType, recordId, category, onClose, onDone, modeAttach }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [pickedId, setPickedId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (q) params.q = q;
      const r = await axios.get(`${API}/attachments`, { params, withCredentials: true });
      // Exclude attachments already on this exact record.
      const filtered = (r.data.items || []).filter(
        (a) => !(a.record_type === recordType && a.record_id === recordId));
      setItems(filtered);
    } catch (e) { toast.error("Failed to load existing attachments"); }
    finally { setLoading(false); }
  }, [q, recordType, recordId]);
  useEffect(() => { load(); }, [load]);

  const attach = async () => {
    if (!pickedId) { toast.error("Pick an attachment first"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/attachments/attach-existing`, {
        source_attachment_id: pickedId,
        new_record_type: recordType, new_record_id: recordId,
        category,
      }, { withCredentials: true });
      toast.success(modeAttach ? "Attached to this record" : "Reused on this record");
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d?.code || "Attach failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalFrame method={modeAttach ? "attach" : "existing"}
                 recordType={recordType} recordId={recordId} category={category}
                 onClose={onClose} wide>
      <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
        <input data-testid="attach-existing-search" value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search file name, category, label…"
                style={{ padding: 10, border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13 }} />
        <div data-testid="attach-existing-list"
              style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 4 }}>
          {loading && <p style={{ padding: 12, fontSize: 12, color: "#64748b" }}>Loading…</p>}
          {!loading && items.length === 0 && (
            <p data-testid="attach-existing-empty" style={{ padding: 12, fontSize: 12, color: "#64748b" }}>
              No existing attachments in your tenant match this search.
            </p>
          )}
          {items.map((a) => (
            <label key={a.id} data-testid={`attach-existing-row-${a.id}`}
                    style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 10,
                             alignItems: "center", padding: "10px 12px",
                             borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                             background: pickedId === a.id ? "#F8F2E1" : "white" }}>
              <input type="radio" name="pick" checked={pickedId === a.id}
                      onChange={() => setPickedId(a.id)} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{a.file_name}</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>
                  {a.record_type} · {a.category} · {a.file_type} ·
                  {" "}{(a.file_size / 1024).toFixed(1)} KB · v{a.version} · {a.review_status}
                </div>
              </div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}${a.preview_url}`}
                  target="_blank" rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ fontSize: 11, color: "#12233F", textDecoration: "underline" }}>Preview</a>
            </label>
          ))}
        </div>
        <p style={{ fontSize: 11, color: "#64748b", margin: 0 }}>
          Reuses the SAME underlying file — no duplicate storage. A new logical row is
          created on this record and audit-linked back to the source.
        </p>
      </div>
      <ModalActions busy={busy} onClose={onClose} onSubmit={attach}
                     submitLabel={modeAttach ? "Attach here" : "Choose"} />
    </ModalFrame>
  );
}


// ---------------------------------------------------------------------------
// TakePhoto — single frame via getUserMedia
// ---------------------------------------------------------------------------
function TakePhotoDialog({ recordType, recordId, category, onClose, onDone }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [caption, setCaption] = useState("");

  useEffect(() => {
    let alive = true;
    async function open() {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" }, audio: false,
        });
        if (!alive) { s.getTracks().forEach((t) => t.stop()); return; }
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (e) {
        setError("Camera unavailable on this device. Use Upload instead.");
      }
    }
    open();
    return () => { alive = false; };
  }, []);
  useEffect(() => () => { if (stream) stream.getTracks().forEach((t) => t.stop()); }, [stream]);

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current, c = canvasRef.current;
    c.width = v.videoWidth || 1280; c.height = v.videoHeight || 720;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    setPreview(c.toDataURL("image/jpeg", 0.9));
  };
  const retake = () => setPreview(null);

  const submit = async () => {
    if (!preview) { toast.error("Capture a photo first"); return; }
    setBusy(true);
    try {
      const blob = await (await fetch(preview)).blob();
      const fd = new FormData();
      fd.append("file", blob, `photo_${Date.now()}.jpg`);
      fd.append("record_type", recordType);
      fd.append("record_id", recordId);
      fd.append("category", category);
      if (caption) fd.append("caption", caption);
      fd.append("captured_at", new Date().toISOString());
      await axios.post(`${API}/attachments/photo`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo captured");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Photo failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalFrame method="photo" recordType={recordType} recordId={recordId}
                 category={category} onClose={onClose} wide>
      <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
        {error && <p data-testid="photo-error" style={{ color: "#dc2626", fontSize: 12 }}>{error}</p>}
        {!error && !preview && (
          <>
            <video ref={videoRef} autoPlay playsInline muted data-testid="photo-video"
                    style={{ width: "100%", background: "#0f172a", borderRadius: 4 }} />
            <button data-testid="photo-capture" onClick={capture}
                    style={{ padding: "10px 14px", background: "#12233F", color: "#F5F1E8",
                             border: "2px solid #C6A15B", borderRadius: 4, cursor: "pointer",
                             fontFamily: "'Space Mono', monospace", fontSize: 12,
                             letterSpacing: 1, textTransform: "uppercase" }}>
              Capture frame
            </button>
          </>
        )}
        {preview && (
          <>
            <img src={preview} data-testid="photo-preview"
                  style={{ width: "100%", borderRadius: 4, border: "1px solid #e2e8f0" }} />
            <input value={caption} onChange={(e) => setCaption(e.target.value)}
                    placeholder="Caption (optional)…" data-testid="photo-caption"
                    style={{ padding: 10, border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13 }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button data-testid="photo-retake" onClick={retake}
                      style={{ padding: "8px 12px", background: "white", color: "#12233F",
                               border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer" }}>
                Retake
              </button>
            </div>
          </>
        )}
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
      <ModalActions busy={busy} onClose={onClose} onSubmit={submit}
                     submitLabel="Save photo" disabled={!preview || !!error} />
    </ModalFrame>
  );
}


// ---------------------------------------------------------------------------
// Scan-Document — multi-page camera capture. Each page uploaded as its own
// photo with a shared `scan-group:<uuid>` label so they can be reviewed together.
// ---------------------------------------------------------------------------
function ScanDocumentDialog({ recordType, recordId, category, onClose, onDone }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    let alive = true;
    async function open() {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" }, audio: false,
        });
        if (!alive) { s.getTracks().forEach((t) => t.stop()); return; }
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (e) {
        setError("Camera unavailable on this device. Use Upload instead.");
      }
    }
    open();
    return () => { alive = false; };
  }, []);
  useEffect(() => () => { if (stream) stream.getTracks().forEach((t) => t.stop()); }, [stream]);

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current, c = canvasRef.current;
    c.width = v.videoWidth || 1280; c.height = v.videoHeight || 720;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    setPages((p) => [...p, c.toDataURL("image/jpeg", 0.9)]);
  };
  const removePage = (i) => setPages((p) => p.filter((_, ix) => ix !== i));

  const submit = async () => {
    if (!pages.length) { toast.error("Capture at least one page"); return; }
    setBusy(true); setPct(0);
    // Shared group id so the pages can be reviewed together downstream.
    const groupId = `scan-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      for (let i = 0; i < pages.length; i += 1) {
        const blob = await (await fetch(pages[i])).blob();
        const fd = new FormData();
        fd.append("file", blob, `scan_${i + 1}.jpg`);
        fd.append("record_type", recordType);
        fd.append("record_id", recordId);
        fd.append("category", category);
        fd.append("caption", `${groupId} · page ${i + 1} of ${pages.length}`);
        fd.append("captured_at", new Date().toISOString());
        await axios.post(`${API}/attachments/photo`, fd, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
        });
        setPct((i + 1) / pages.length);
      }
      toast.success(`Scanned ${pages.length} page${pages.length === 1 ? "" : "s"}`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Scan upload failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalFrame method="scan" recordType={recordType} recordId={recordId}
                 category={category} onClose={onClose} wide>
      <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
        {error && <p data-testid="scan-error" style={{ color: "#dc2626", fontSize: 12 }}>{error}</p>}
        {!error && (
          <>
            <video ref={videoRef} autoPlay playsInline muted data-testid="scan-video"
                    style={{ width: "100%", background: "#0f172a", borderRadius: 4 }} />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button data-testid="scan-capture" onClick={capture}
                      style={{ padding: "10px 14px", background: "#12233F", color: "#F5F1E8",
                               border: "2px solid #C6A15B", borderRadius: 4, cursor: "pointer",
                               fontFamily: "'Space Mono', monospace", fontSize: 12,
                               letterSpacing: 1, textTransform: "uppercase" }}>
                Capture page
              </button>
              <span data-testid="scan-page-count" style={{ fontSize: 12, color: "#64748b" }}>
                {pages.length} page{pages.length === 1 ? "" : "s"} captured
              </span>
            </div>
            {pages.length > 0 && (
              <div data-testid="scan-thumbnails"
                    style={{ display: "flex", gap: 6, overflowX: "auto", padding: 4 }}>
                {pages.map((p, i) => (
                  <div key={i} data-testid={`scan-page-${i}`}
                        style={{ position: "relative", flex: "0 0 auto" }}>
                    <img src={p} style={{ height: 100, borderRadius: 4, border: "1px solid #e2e8f0" }} />
                    <button onClick={() => removePage(i)}
                            data-testid={`scan-page-remove-${i}`}
                            style={{ position: "absolute", top: 2, right: 2,
                                     width: 20, height: 20, borderRadius: 9999, border: "none",
                                     background: "#dc2626", color: "white", cursor: "pointer",
                                     fontSize: 12, lineHeight: "20px" }}>×</button>
                    <span style={{ position: "absolute", bottom: 2, left: 2, background: "#0009",
                                    color: "white", fontSize: 10, padding: "0 4px", borderRadius: 2 }}>
                      {i + 1}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {busy && pct > 0 && (
          <div data-testid="scan-progress" style={{ height: 6, background: "#e2e8f0", borderRadius: 3 }}>
            <div style={{ width: `${Math.round(pct * 100)}%`, height: "100%",
                          background: "#C6A15B", transition: "width 200ms ease" }} />
          </div>
        )}
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
      <ModalActions busy={busy} onClose={onClose} onSubmit={submit}
                     submitLabel={`Save ${pages.length || "…"} pages`}
                     disabled={!pages.length || !!error} />
    </ModalFrame>
  );
}


// ---------------------------------------------------------------------------
// Shared modal frame
// ---------------------------------------------------------------------------
function ModalFrame({ method, recordType, recordId, category, onClose, children, wide }) {
  return (
    <div role="dialog" data-testid={`intake-dialog-${method}`}
         style={{ position: "fixed", inset: 0, background: "#0006",
                  display: "grid", placeItems: "center", zIndex: 200 }}>
      <div style={{ width: wide ? 640 : 520, maxWidth: "94vw",
                    background: "white", borderRadius: 6,
                    padding: 20, borderTop: "4px solid #C6A15B",
                    maxHeight: "90vh", overflowY: "auto" }}>
        <h2 style={{ margin: 0, fontFamily: "Playfair Display, serif", color: "#12233F" }}>
          {METHOD_META[method].label}
        </h2>
        <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
          {recordType} · {(recordId || "").slice(0, 8)} · {category}
        </p>
        {children}
      </div>
    </div>
  );
}


function ModalActions({ busy, onClose, onSubmit, submitLabel = "Submit", disabled }) {
  return (
    <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", gap: 10 }}>
      <button data-testid="intake-cancel" onClick={onClose}
              style={{ padding: "8px 14px", background: "white", color: "#12233F",
                       border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer" }}>
        Close
      </button>
      <button data-testid="intake-submit" onClick={onSubmit} disabled={busy || disabled}
              style={{ padding: "8px 18px", background: "#12233F", color: "#F5F1E8",
                       border: "2px solid #C6A15B", borderRadius: 4,
                       cursor: (busy || disabled) ? "not-allowed" : "pointer",
                       opacity: (busy || disabled) ? 0.55 : 1,
                       fontFamily: "'Space Mono', monospace", fontSize: 12,
                       letterSpacing: 1, textTransform: "uppercase" }}>
        {busy ? "Working…" : submitLabel}
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Attachments panel — reads back the record's attachments.
// ---------------------------------------------------------------------------
export function AttachmentsPanel({ recordType, recordId, reload }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/attachments`,
                                  { params: { record_type: recordType, record_id: recordId },
                                    withCredentials: true });
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  }, [recordType, recordId]);
  useEffect(() => { load(); }, [load, reload]);

  return (
    <div data-testid={`attachments-panel-${recordType}-${recordId}`}
         style={{ marginTop: 12 }}>
      {loading && <p style={{ fontSize: 12, color: "#64748b" }}>Loading…</p>}
      {!loading && items.length === 0 && (
        <p data-testid="attachments-empty" style={{ fontSize: 12, color: "#64748b" }}>
          No attachments yet.
        </p>
      )}
      {items.map((a) => (
        <div key={a.id} data-testid={`attachment-row-${a.id}`}
             style={{ padding: 10, border: "1px solid #e2e8f0", borderRadius: 4,
                      marginBottom: 6, display: "flex", justifyContent: "space-between",
                      gap: 10, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{a.file_name}</div>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>
              {a.category} · {a.file_type} · {(a.file_size / 1024).toFixed(1)} KB ·
              v{a.version} · <b>{a.review_status}</b> · scan: {a.scan_state} ·
              conf: {a.confidentiality}
              {a.label && <> · <i>{a.label}</i></>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <a data-testid={`attachment-preview-${a.id}`}
                href={`${process.env.REACT_APP_BACKEND_URL}${a.preview_url}`}
                target="_blank" rel="noreferrer"
                style={{ fontSize: 11, color: "#12233F", textDecoration: "underline" }}>
              Preview
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}
